import PropTypes from 'prop-types';
import React from 'react';
import { useField } from 'formik';

import { __t, mapDynamicFieldToName } from '@mssgme/shared';
import { FormField, Input, Textarea, SelectSimple, RadioGroup, CheckboxGroup, CountrySelect } from '@mssgme/ui';

import { useCustomInputTheme, useInputTheme } from '../../hooks';

import styles from './DynamicFormInput.scss';

const SIMPLE_TEXT_INPUT_MAX_LENGTH = 250;

const specificComponents = {
    select: SelectSimple,
    radio: RadioGroup,
    checkbox: CheckboxGroup,
    country: CountrySelect,
};

const inputSpecificTypes = {
    name: 'text',
    phone: 'tel',
};

const handleEnterKey = (e) => {
    if (e.key === 'Enter') {
        e.preventDefault();
    }
};

export const isFieldSupport = (type, feature) => {
    switch (feature) {
        case 'paneProp':
            return ['radio', 'checkbox'].includes(type);
        case 'maxLength':
            return !['date', 'time', 'number'].includes(type);
        case 'hasPlaceholderOption':
            return type === 'select';
        default:
            return false;
    }
};

const getFieldProps = ({ name, label, type, required, maxLength, options }) => {
    const specificComponent = specificComponents[type];
    const isTextArea = maxLength > SIMPLE_TEXT_INPUT_MAX_LENGTH && type === 'text';
    const commonProps = {
        name,
        label: label || type,
        required,
    };

    if (specificComponent) {
        let optionsArray = options?.split(/\r?\n/).filter(Boolean).map((option) => ({ label: option, value: option }));

        if (isFieldSupport(type, 'hasPlaceholderOption')) {
            optionsArray = [{ label: __t('not_selected'), value: '' }, ...optionsArray];
        }

        return {
            ...commonProps,
            component: specificComponent,
            options: optionsArray,
            pane: isFieldSupport(type, 'paneProp') || undefined,
        };
    }

    if (isTextArea) {
        return {
            ...commonProps,
            component: Textarea,
            maxLength,
        };
    }

    return {
        ...commonProps,
        component: Input,
        maxLength: isFieldSupport(type, 'maxLength') ? maxLength : undefined,
        type: inputSpecificTypes[type] || type,
        onKeyPress: handleEnterKey,
        ...(type === 'number' && { min: 0, inputMode: 'numeric', pattern: '[0-9]*' }),
    };
};

export const DynamicFormInput = ({ index, input, as, defaultMaxSize, theme }) => {
    const defaultStyle = useInputTheme(theme);
    const customStyle = useCustomInputTheme(theme);
    const style = customStyle || defaultStyle;
    const maxLength = input.size || defaultMaxSize;
    const name = mapDynamicFieldToName(input, index);
    const fieldProps = getFieldProps({ ...input, maxLength, name });
    const [field, meta] = useField(name);

    return (
        <FormField
            shakeError
            as={as}
            errors={meta.touched && meta.error}
            style={style.input}
            errorStyle={style.error}
            inputClassName={styles.input}
            labelClassName={styles.label}
            {...field}
            {...fieldProps}
        />
    );
};

DynamicFormInput.propTypes = {
    input: PropTypes.shape({
        _id: PropTypes.string,
        label: PropTypes.string,
        required: PropTypes.bool,
        size: PropTypes.number,
        options: PropTypes.string,
        type: PropTypes.string,
    }).isRequired,
    index: PropTypes.number.isRequired,
    as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
    type: PropTypes.string,
    defaultMaxSize: PropTypes.number.isRequired,
    theme: PropTypes.object,
};
